import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { applyTheme } from '@/design'
import { isNonProduction } from '@/lib/environment'
import { useCurrentTenant } from '@/lib/access'
import { FreshnessProvider } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { ImpersonationBanner } from './components/ImpersonationBanner'
import { Sidebar } from './components/Sidebar'
import { TopBar } from './components/TopBar'
import './shell.css'

function NonProductionBanner() {
  const { filters, setFilters } = useGlobalFilters()
  if (!isNonProduction(filters.environments)) return null
  return (
    <div className="ci-banner" role="status">
      Showing non-production data ({filters.environments.join(', ')}). These are not your reportable figures.
      <button type="button" className="ci-button" onClick={() => setFilters({ environments: [] })}>
        Back to production only
      </button>
    </div>
  )
}

/** The application frame: sidebar, top bar and the routed page. */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { pathname } = useLocation()
  const tenant = useCurrentTenant()
  const accent = tenant.data?.accentColor ?? null
  const tenantName = tenant.data?.name
  const menuRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(false)

  useEffect(() => setDrawerOpen(false), [pathname])

  // Closing the drawer hands focus back to the button that opened it, unless
  // the viewer has already moved focus somewhere else on purpose.
  useEffect(() => {
    if (wasOpen.current && !drawerOpen) {
      const active = document.activeElement
      const sidebar = document.getElementById('ci-sidebar')
      if (!active || active === document.body || sidebar?.contains(active)) menuRef.current?.focus()
    }
    wasOpen.current = drawerOpen
  }, [drawerOpen])

  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  useEffect(() => {
    applyTheme(document.documentElement, { accent })
  }, [accent])

  useEffect(() => {
    document.title = tenantName ? `${tenantName} · Competition Insights` : 'TmT Competition Insights'
  }, [tenantName])

  return (
    <FreshnessProvider>
    <div className="ci-shell" data-drawer-open={drawerOpen ? 'true' : 'false'}>
      <a className="ci-skip-link" href="#ci-main">
        Skip to content
      </a>
      <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      {drawerOpen ? <div className="ci-shell__scrim" aria-hidden="true" onClick={() => setDrawerOpen(false)} /> : null}
      {/* While the drawer is open the page behind it is out of reach, as it is out of sight. */}
      <div className="ci-shell__body" inert={drawerOpen || undefined}>
        <TopBar drawerOpen={drawerOpen} onMenu={() => setDrawerOpen((value) => !value)} menuRef={menuRef} />
        <ImpersonationBanner />
        <NonProductionBanner />
        <main id="ci-main" className="ci-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
    </FreshnessProvider>
  )
}
